import type { useDateInputModel } from './useModelValue'
import type { ISODateTimeString } from '@gat-ship/types'
import { Temporal } from '@js-temporal/polyfill'
import { computed, toValue, type MaybeRefOrGetter, type Ref } from 'vue'
import { getCurrentTimeZoneId } from './date-utils'
import type { GatDateEditProps } from '@/components/gat-date-edit/gat-date-edit.vue'

export function isValidMinute(minute: number): boolean {
  return minute >= 0 && minute <= 59
}

export function parseMinuteFromString(minutePart: string | undefined): number | undefined {
  if (!minutePart) {
    return undefined
  }
  const minute = Number(minutePart)
  if (isValidMinute(minute)) {
    return minute
  }
  return undefined
}

export function isValidHour(hour: number): boolean {
  return hour >= 0 && hour <= 23
}

export function parseHourFromString(hourPart: string | undefined): number | undefined {
  // The hour part can be 1 or 2 digits
  if (!hourPart) {
    return undefined
  }

  // Handle both 1 and 2 digit strings
  const hour = Number(hourPart)
  if (isValidHour(hour)) {
    return hour
  }

  return undefined
}

export function useTimeInput(
  props: {
    modelValue: MaybeRefOrGetter<GatDateEditProps['modelValue']>
    timeZone: MaybeRefOrGetter<GatDateEditProps['timeZone']>
  },
  updateModel: ReturnType<typeof useDateInputModel>['updateModel']
) {
  const model = computed(() => toValue(props.modelValue))
  const timeZone = computed(() => toValue(props.timeZone) ?? getCurrentTimeZoneId())

  const timeInputValue = computed(() => {
    if (!model.value) {
      return ''
    }
    return Temporal.Instant.from(model.value)
      .toZonedDateTimeISO(timeZone.value)
      .toLocaleString(Intl.DateTimeFormat().resolvedOptions().locale, {
        timeStyle: 'short',
        hour12: false
      })
  })

  function handleTimeChange(e: FocusEvent) {
    const timeInput = e.target as HTMLInputElement
    if (timeInput.value === timeInputValue.value) {
      return
    }
    const timeValue = timeInput.value.replaceAll(':', '#').replaceAll(' ', '#')
    let [hours, minutes] = timeValue.split('#')
    if (timeValue.length >= 3 && !timeValue.includes('#')) {
      hours = timeValue.replaceAll('#', '').substring(0, 2)
      minutes = timeValue.replaceAll('#', '').substring(2, 4)
    }
    if (!model.value) {
      const time = Temporal.Now.zonedDateTimeISO(timeZone.value).with({
        hour: parseHourFromString(hours),
        minute: parseMinuteFromString(minutes) ?? 0
      })
      updateModel(time.toInstant().toString() as ISODateTimeString, {
        timeZone: timeZone.value
      })
      return
    }
    const time = Temporal.Instant.from(model.value)
    const zonedDateTime = time.toZonedDateTimeISO(timeZone.value).with({
      hour: parseHourFromString(hours),
      minute: parseMinuteFromString(minutes) ?? 0
    })
    console.table({ zonedDateTime, timeZone: timeZone.value })
    updateModel(zonedDateTime.toInstant().toString() as ISODateTimeString, {
      timeZone: timeZone.value
    })
  }

  return {
    timeInputValue,
    handleTimeChange
  }
}
